import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  daoTransactionAddAndPayRazorpayStep1Post,
  daoTransactionAddAndPayRazorpayStep2Post,
  daoTransactionAddRazorpayStep1Post,
  daoTransactionAddRazorpayStep2Post,
  daoTransactionCancelRazorpayDelete,
  daoTransactionUseRazorpayPost,
} from 'dao/transaction-razorpay-dao';
import { Action } from 'types/action';
import { Thunk } from 'types/root';
import {
  AddAndPayRazorpayStep1Resp,
  AddAndPayRazorpayStep2Payload,
  AddAndPayRazorpayStep2Resp,
  AddRazorpayStep1Resp,
  AddRazorpayStep2Payload,
  AddRazorpayStep2Resp,
  CancelRazorpayTransactionPayload,
  TransactionsRazorpayState,
  UseRazorpayPayload,
  UseRazorpayResp,
} from 'types/transaction-razorpay';

export const postTransactionAddRazorpayStep1 = createAsyncThunk<AddRazorpayStep1Resp, void, Thunk>(
  'transactions/addRazorpayStep1',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAddRazorpayStep1Post(accountKey, sessionId);
    return response;
  },
);

export const postTransactionAddRazorpayStep2 = createAsyncThunk<AddRazorpayStep2Resp, AddRazorpayStep2Payload, Thunk>(
  'transactions/addRazorpayStep2',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAddRazorpayStep2Post(accountKey, sessionId, payload);
    return response;
  },
);

export const postTransactionAddAndPayRazorpayStep1 = createAsyncThunk<AddAndPayRazorpayStep1Resp, void, Thunk>(
  'transactions/addAndPayRazorpayStep1',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAddAndPayRazorpayStep1Post(accountKey, sessionId);
    return response;
  },
);

export const postTransactionAddAndPayRazorpayStep2 = createAsyncThunk<
  AddAndPayRazorpayStep2Resp,
  AddAndPayRazorpayStep2Payload,
  Thunk
>('transactions/addAndPayRazorpayStep2', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const {
    globalWrapper: { accountKey, sessionId },
  } = state;
  const response = await daoTransactionAddAndPayRazorpayStep2Post(accountKey, sessionId, payload);
  return response;
});

export const postTransactionUseRazorPay = createAsyncThunk<UseRazorpayResp, UseRazorpayPayload, Thunk>(
  'transactions/useRazorPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionUseRazorpayPost(accountKey, sessionId, payload);
    return response;
  },
);

export const cancelRazorpayTransaction = createAsyncThunk<void, CancelRazorpayTransactionPayload, Thunk>(
  'transactions/cancelRazorpayTransaction',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    await daoTransactionCancelRazorpayDelete(accountKey, sessionId, payload);
  },
);

const initialState: TransactionsRazorpayState = {
  isAddPaymentLoading: false,
  isUsePaymentLoading: false,
  isAddAndPayPaymentLoading: false,
  addRazorpayStep1Resp: {} as AddRazorpayStep1Resp,
  addRazorpayStep2Resp: {} as AddRazorpayStep2Resp,
  addAndPayRazorpayStep1Resp: {} as AddAndPayRazorpayStep1Resp,
  addAndPayRazorpayStep2Resp: {} as AddAndPayRazorpayStep2Resp,
  useRazorPayResp: {} as UseRazorpayResp,
};

const transactionsRazorpaySlice = createSlice({
  name: 'transactions-razorpay',
  initialState,
  reducers: {
    resetAddAndPayResponse: (state) => {
      state.addAndPayRazorpayStep1Resp = {} as AddAndPayRazorpayStep1Resp;
      state.addAndPayRazorpayStep2Resp = {} as AddAndPayRazorpayStep2Resp;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(postTransactionAddRazorpayStep1.pending, (state) => {
        state.isAddPaymentLoading = true;
      })
      .addCase(postTransactionAddRazorpayStep1.fulfilled, (state, action: Action) => {
        state.isAddPaymentLoading = false;
        state.addRazorpayStep1Resp = action.payload;
      })
      .addCase(postTransactionAddRazorpayStep1.rejected, (state) => {
        state.isAddPaymentLoading = false;
      })
      .addCase(postTransactionAddRazorpayStep2.pending, (state) => {
        state.isAddPaymentLoading = true;
      })
      .addCase(postTransactionAddRazorpayStep2.fulfilled, (state, action: Action) => {
        state.isAddPaymentLoading = false;
        state.addRazorpayStep2Resp = action.payload;
      })
      .addCase(postTransactionAddRazorpayStep2.rejected, (state) => {
        state.isAddPaymentLoading = false;
      })
      .addCase(postTransactionAddAndPayRazorpayStep1.pending, (state) => {
        state.isAddAndPayPaymentLoading = true;
      })
      .addCase(postTransactionAddAndPayRazorpayStep1.fulfilled, (state, action: Action) => {
        state.isAddAndPayPaymentLoading = false;
        state.addAndPayRazorpayStep1Resp = action.payload;
      })
      .addCase(postTransactionAddAndPayRazorpayStep1.rejected, (state) => {
        state.isAddAndPayPaymentLoading = false;
      })
      .addCase(postTransactionAddAndPayRazorpayStep2.pending, (state) => {
        state.isAddAndPayPaymentLoading = true;
      })
      .addCase(postTransactionAddAndPayRazorpayStep2.fulfilled, (state, action: Action) => {
        state.isAddAndPayPaymentLoading = false;
        state.addAndPayRazorpayStep2Resp = action.payload;
      })
      .addCase(postTransactionAddAndPayRazorpayStep2.rejected, (state) => {
        state.isAddAndPayPaymentLoading = false;
      })
      .addCase(postTransactionUseRazorPay.pending, (state) => {
        state.isUsePaymentLoading = true;
      })
      .addCase(postTransactionUseRazorPay.fulfilled, (state, action: Action) => {
        state.isUsePaymentLoading = false;
        state.useRazorPayResp = action.payload;
      })
      .addCase(postTransactionUseRazorPay.rejected, (state) => {
        state.isUsePaymentLoading = false;
      });
  },
});

export const { resetAddAndPayResponse } = transactionsRazorpaySlice.actions;

export default transactionsRazorpaySlice.reducer;
